/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import "katex/dist/katex.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, COMMAND_PRIORITY_EDITOR, createCommand, LexicalCommand, LexicalEditor } from "lexical";
import { useCallback, useEffect } from "react";
import * as React from "react";
import { $insertNodeToNearestRoot } from "@lexical/utils";

import { $createEquationNode, EquationNode } from "../../nodes/EquationNode/EquationNode";
import KatexEquationAlterer from "../../ui/KatexEquationAlterer";
import useEditorStore from "store/useEditorStore";

type CommandPayload = {
  equation: string;
  inline: boolean;
};

export const INSERT_EQUATION_COMMAND: LexicalCommand<CommandPayload> = createCommand("INSERT_EQUATION_COMMAND");

export function InsertEquationDialog({ activeEditor, onClose }: { activeEditor: LexicalEditor; onClose: () => void }): JSX.Element {
  const { focusEditor } = useEditorStore();
  const onEquationConfirm = useCallback(
    (equation: string, inline: boolean) => {
      focusEditor();
      activeEditor.dispatchCommand(INSERT_EQUATION_COMMAND, { equation, inline });
      onClose();
    },
    [activeEditor, onClose]
  );

  return <KatexEquationAlterer onConfirm={onEquationConfirm} />;
}

export default function EquationsPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([EquationNode])) {
      throw new Error("EquationsPlugins: EquationsNode not registered on editor");
    }

    return editor.registerCommand<CommandPayload>(
      INSERT_EQUATION_COMMAND,
      (payload) => {
        const { equation, inline = false } = payload;
        const equationNode = $createEquationNode(equation, inline);

        $insertNodeToNearestRoot(equationNode);
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return null;
}
